import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, Chip, Divider,
} from '@mui/material';
import { ArrowBack, Close } from '@mui/icons-material';
import ScoutCard from '../../components/scout/ScoutCard';
import ScoutThreadView from '../../components/scout/ScoutThreadView';
import { scoutService } from '../../services/scoutService';

const ScoutSentPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [threads, setThreads] = useState<any[]>([]);
  const [selectedThread, setSelectedThread] = useState<string | null>(null);
  const [threadData, setThreadData] = useState<any>(null);
  const [threadMessages, setThreadMessages] = useState<any[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    scoutService.list('company')
      .then(setThreads)
      .catch((err) => setError(err.response?.data?.message || 'スカウト一覧の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const loadThread = useCallback(async (threadId: string) => {
    setThreadLoading(true);
    try {
      const data = await scoutService.getThread(threadId);
      setThreadData(data.thread);
      setThreadMessages(data.messages);
    } catch (err) {
      console.error(err);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const handleOpenThread = (threadId: string) => {
    setSelectedThread(threadId);
    loadThread(threadId);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="md">
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/company/dashboard')}
            sx={{ color: 'white', mb: 1 }}>
            ダッシュボード
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            送信済みスカウト
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {threads.length}件のスカウト
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : threads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              まだスカウトを送信していません
            </Typography>
            <Button variant="contained" onClick={() => navigate('/company/search')}>
              アスリートを検索する
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {threads.map((thread) => (
              <ScoutCard
                key={thread.threadId}
                thread={thread}
                role="company"
                onClick={() => handleOpenThread(thread.threadId)}
              />
            ))}
          </Box>
        )}
      </Container>

      {/* スレッドダイアログ */}
      <Dialog
        open={!!selectedThread}
        onClose={() => setSelectedThread(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {threadData?.subject}
            </Typography>
            {threadData?.status && (
              <Chip label={threadData.status} size="small" sx={{ mt: 0.5 }} />
            )}
          </Box>
          <Button onClick={() => setSelectedThread(null)} startIcon={<Close />}>閉じる</Button>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {threadLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <ScoutThreadView
              threadId={selectedThread!}
              messages={threadMessages}
              role="company"
              disabled={['blocked', 'declined'].includes(threadData?.status)}
              onMessageSent={() => selectedThread && loadThread(selectedThread)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScoutSentPage;
